import React from 'react'
import { useQuery } from '@tanstack/react-query'
import SavedTasksCard, { CardTagPropsType } from './SavedTasksCard.tsx'
import styles from './SavedTasksScreen.module.less'
import { useNavigate } from 'react-router-dom'
import { useUserProfile } from 'shared/auth/hooks/use-user-profile/useUserProfile'
import { getOnHoldAssignmentScopes } from 'Services/tasks.service.ts'
import { EmptyStateIndicator } from 'Components/common/EmptyStaleIndicator/EmptyStaleIndicator.tsx'
import { CardListSkeleton } from '../CardListSkeleton/CardListSkeleton.tsx'

type ScopeType = {
    projectNickname: string
    workflowUseCase: string
    stepName: string
    stepType: string
    stepId: string
}

type ProcessedProject = {
    title: string
    tags: CardTagPropsType[]
    savedTasks: number
    scope: ScopeType
}

const SavedTasksScreen: React.FC = () => {
    const navigate = useNavigate()
    const { userProfile } = useUserProfile()
    const userId = userProfile?.user?.user_id

    const {
        data: savedProjects = [],
        isLoading,
        isError,
        error,
    } = useQuery({
        queryKey: ['assignmentScopes', userId],
        queryFn: async (): Promise<ProcessedProject[]> => {
            if (!userId) {
                throw new Error('User ID is required')
            }
            const assignmentScope = await getOnHoldAssignmentScopes(userId)
            if (!assignmentScope) {
                throw new Error('Failed to load the assignment. Please try again')
            }
            return assignmentScope.map(scope => {
                return {
                    title: scope.projectNickname,
                    tags: [
                        { title: scope.workflowUseCase, type: 'primary' },
                        { title: scope.stepName, type: scope.stepType.toLowerCase() },
                    ] as CardTagPropsType[],
                    savedTasks: scope.taskCount || 0,
                    scope: {
                        projectNickname: scope.projectNickname,
                        workflowUseCase: scope.workflowUseCase,
                        stepName: scope.stepName,
                        stepType: scope.stepType,
                        stepId: scope.stepId,
                    },
                }
            })
        },
        enabled: !!userId,
        staleTime: 0,
    })

    if (isLoading) {
        return <CardListSkeleton />
    }

    if (isError) {
        return (
            <div className={styles.screenWrapper}>
                <EmptyStateIndicator
                    label={'Error loading tasks'}
                    caption={`${
                        error instanceof Error
                            ? error.message
                            : 'An error occurred while loading tasks'
                    }`}
                />
            </div>
        )
    }

    return (
        <div className={styles.container}>
            {savedProjects.length === 0 ? (
                <EmptyStateIndicator
                    label={'No tasks found'}
                    caption="Please refresh or check again later to see if any tasks that are on hold"
                />
            ) : (
                <div className={styles.screenWrapper}>
                    <div className={styles.cardsContainer}>
                        {savedProjects.map((project, index) => (
                            <SavedTasksCard
                                key={project.title + index}
                                title={project.title}
                                tags={project.tags}
                                savedTasks={project.savedTasks}
                                onClick={() => navigate(`/saved/${project.scope.stepId}`)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default SavedTasksScreen
